/**
 * Shared lifecycle state of a quote in the Superfaktura/CRM document chain.
 *
 * Single source of truth used by:
 *  - the UI to show/hide row actions (generate deposit, generate final invoice, generate protocol)
 *  - backend preconditions (idempotency in create-final-invoice)
 *
 * The chain we model:
 *   quote → deposit_invoice (zálohová) → tax_document (daňový doklad) → invoice (Konečná faktúra)
 *                                                                    ↓
 *                                                          contract: odovzdávací protokol
 *                                                          contract: zmluva o dielo (under quote/deposit)
 */
import {
  isDocumentRelatedToDocument,
  isDocumentRelatedToQuote,
  type RelationDoc,
} from "./document-relations";

/** Minimal contract shape for lifecycle checks (matches both the DB row and the UI variant). */
export interface LifecycleContract {
  id: string;
  template_id?: string | null;
  template_name?: string | null;
  document_ids?: string[] | null;
}

export type LifecycleNextStep =
  /** No deposit yet → user can generate Zálohová faktúra. */
  | "create_deposit"
  /** Deposit exists, but no tax_document yet (we wait for SF "Daňový doklad k prijatej platbe"). */
  | "await_tax_doc"
  /** Tax doc is in → user can generate the Konečná faktúra (and protocol). */
  | "create_final_invoice"
  /** Konečná already exists, but the handover protocol does not → user can just generate the protocol. */
  | "generate_protocol_only"
  /** Everything is in place. */
  | "done";

export interface LifecycleState {
  hasDeposit: boolean;
  hasTaxDoc: boolean;
  hasFinal: boolean;
  /** Zmluva o dielo (signed contract). */
  hasContract: boolean;
  /** Odovzdávací protokol (handover). */
  hasProtocol: boolean;
  nextStep: LifecycleNextStep;
}

const PROTOCOL_TEMPLATE_ID = "odovzdavaci-protokol";
const ZMLUVA_TEMPLATE_ID = "zmluva-o-dielo";

function isProtocolContract(c: LifecycleContract): boolean {
  if (c.template_id === PROTOCOL_TEMPLATE_ID) return true;
  const name = (c.template_name ?? "").toLowerCase();
  return name.includes("odovzd") || name.includes("preber") || name.includes("protokol");
}

function isZmluvaContract(c: LifecycleContract): boolean {
  if (c.template_id === ZMLUVA_TEMPLATE_ID) return true;
  const name = (c.template_name ?? "").toLowerCase();
  return name.includes("zmluva");
}

/**
 * Compute the lifecycle state for a single quote inside a deal.
 *
 * @param quote      the root quote (must have document_type === 'quote')
 * @param allDocs    all superfaktura_documents rows for the deal
 * @param contracts  all contract rows for the deal (optional)
 */
export function documentLifecycle<T extends RelationDoc>(
  quote: T,
  allDocs: ReadonlyArray<T>,
  contracts: ReadonlyArray<LifecycleContract> = []
): LifecycleState {
  const quoteId = quote.id;
  const quoteNum = quote.document_number ?? quote.sf_document_id;
  const quoteSf = quote.sf_document_id;

  const deposits = allDocs.filter(
    (d) =>
      d.id !== quoteId &&
      (d.document_type === "deposit_invoice" || d.document_type === "proforma") &&
      isDocumentRelatedToQuote(d, quoteId, quoteNum, quoteSf)
  );
  const hasDeposit = deposits.length > 0;

  // tax_document either points at the deposit, or at the quote directly (rare)
  const taxDocs = allDocs.filter((d) => {
    if (d.id === quoteId) return false;
    if (d.document_type !== "tax_document") return false;
    if (deposits.some((dep) => isDocumentRelatedToDocument(d, dep.id, dep))) return true;
    return isDocumentRelatedToQuote(d, quoteId, quoteNum, quoteSf);
  });
  const hasTaxDoc = taxDocs.length > 0;

  // Konečná: invoice that points to the quote directly OR via a deposit
  const finals = allDocs.filter((d) => {
    if (d.id === quoteId) return false;
    if (d.document_type !== "invoice") return false;
    if (isDocumentRelatedToQuote(d, quoteId, quoteNum, quoteSf)) return true;
    return deposits.some((dep) => isDocumentRelatedToDocument(d, dep.id, dep));
  });
  const hasFinal = finals.length > 0;

  // contracts: anything attached to a doc inside this quote's subtree
  const subtreeIds = new Set<string>([
    quoteId,
    ...deposits.map((d) => d.id),
    ...taxDocs.map((d) => d.id),
    ...finals.map((d) => d.id),
  ]);
  const isAttachedToSubtree = (c: LifecycleContract) =>
    Array.isArray(c.document_ids) && c.document_ids.some((id) => subtreeIds.has(id));

  const hasContract = contracts.some((c) => isAttachedToSubtree(c) && isZmluvaContract(c));
  const hasProtocol = contracts.some((c) => isAttachedToSubtree(c) && isProtocolContract(c));

  let nextStep: LifecycleNextStep;
  if (!hasDeposit) nextStep = "create_deposit";
  else if (hasFinal && !hasProtocol) nextStep = "generate_protocol_only";
  else if (hasFinal && hasProtocol) nextStep = "done";
  else if (hasTaxDoc) nextStep = "create_final_invoice";
  else nextStep = "await_tax_doc";

  return { hasDeposit, hasTaxDoc, hasFinal, hasContract, hasProtocol, nextStep };
}
